/* src/ui/component.ts — the component base (PLAN.md Part 4 §2, TASKS.md 0.5).
 *
 * A component owns one root element and a set of store subscriptions that are released on
 * unmount. It does not diff. Each component hand-picks which of its own nodes are hot
 * (money, ticker rows, gauges — changing every poll) and writes to those nodes directly;
 * everything else is built once at render time. Next to a WebGL frame budget that is the
 * right trade: a `.textContent` write is O(1) and allocation-free, where a tree diff
 * allocates and walks on every change regardless of how little moved.
 *
 * ── Deviation from PLAN.md Part 4 §2, deliberate ──────────────────────────────────────
 * The plan has the constructor call `this.render()` and assign the result to a `readonly el`
 * field. That breaks under standard ES2022 class-field semantics (`useDefineForClassFields`,
 * required by our target). A subclass field declared `private valueNode!: Text` emits a bare
 * `valueNode;` in the subclass body, which runs *after* `super()` returns — so it resets to
 * `undefined` whatever `render()` assigned during construction. The plan's own MoneyTicker
 * example is exactly this shape and would have read `undefined` on its first update.
 *
 * Fix: `el` is a lazy getter. `render()` runs on first access, by which point every subclass
 * field has initialised. Call sites are unchanged (`component.el`), and subclasses may keep
 * using definite-assignment fields for their hot nodes.
 */

import { gameStore } from '../core/store.ts';
import type { GameState } from '../core/types.ts';

export abstract class Component<Props = unknown> {
  #el: HTMLElement | null = null;
  #unsubs: Array<() => void> = [];
  #mounted = false;

  protected readonly props: Props;

  constructor(props: Props) {
    this.props = props;
  }

  /** Built on first access, then cached. Safe to read before mount. */
  get el(): HTMLElement {
    this.#el ??= this.render();
    return this.#el;
  }

  protected abstract render(): HTMLElement;

  /**
   * Subscribe to a slice of game state for the lifetime of this component.
   * The selector must tolerate `null` — it runs before the first poll resolves.
   */
  protected bind<U>(
    selector: (s: GameState | null) => U,
    onChange: (next: U) => void,
  ): void {
    this.#unsubs.push(gameStore.select(selector, onChange));
  }

  /** Register any other teardown (bus subscriptions, timers, observers). */
  protected onCleanup(fn: () => void): void {
    this.#unsubs.push(fn);
  }

  mount(parent: HTMLElement): void {
    if (this.#mounted) return;
    parent.appendChild(this.el);
    this.#mounted = true;
    this.onMount?.();
  }

  unmount(): void {
    if (!this.#mounted) return;
    this.#mounted = false;
    for (const unsub of this.#unsubs) unsub();
    this.#unsubs.length = 0;
    this.onUnmount?.();
    this.el.remove();
  }

  get mounted(): boolean {
    return this.#mounted;
  }

  protected onMount?(): void;
  protected onUnmount?(): void;
}
